import apiClient from "../../../../shared/infrastructure/api/client";
import {
    NotificationChannel,
    NotificationKind,
    NotificationOutcome,
    NotificationRecipient,
    PaymentChangeSource,
    SessionCreatedEvent,
    SessionEvent,
    SessionEventActor,
    SessionPaymentStatus,
    SessionProgressStatus,
} from "../../domain/sessionEvent";

export type SessionEventDTO = {
    id: string;
    type: string;
    occurred_at: string;
    actor_role: string | null;
    details: Record<string, unknown>;
};

const ACTORS: readonly SessionEventActor[] = ["therapist", "patient"];
const STATUSES: readonly SessionProgressStatus[] = ["pending", "finished", "cancelled"];
const ORIGINS: readonly SessionCreatedEvent["origin"][] = ["booking", "direct", "import"];
const PAYMENT_STATUSES: readonly SessionPaymentStatus[] = ["pending", "paid"];
const PAYMENT_SOURCES: readonly PaymentChangeSource[] = ["session_edit", "payment_record", "online_payment"];
const KINDS: readonly NotificationKind[] = ["confirmation", "reschedule", "cancellation", "reminder"];
const CHANNELS: readonly NotificationChannel[] = ["email", "whatsapp"];
const RECIPIENTS: readonly NotificationRecipient[] = ["patient", "therapist"];
const OUTCOMES: readonly NotificationOutcome[] = ["sent", "failed", "opened"];

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | null {
    return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

function text(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

/** One event, or null when its type or details are not what we know. */
function toSessionEvent(dto: SessionEventDTO): SessionEvent | null {
    const base = { id: dto.id, occurredAt: new Date(dto.occurred_at), actor: oneOf(ACTORS, dto.actor_role) };
    const details = dto.details ?? {};
    switch (dto.type) {
        case "created": {
            const initialStatus = oneOf(STATUSES, details.initial_status);
            const origin = oneOf(ORIGINS, details.origin);
            if (!initialStatus || !origin) return null;
            return { ...base, type: "created", origin, initialStatus, bookedBy: oneOf(ACTORS, details.booked_by) };
        }
        case "status_changed": {
            const from = oneOf(STATUSES, details.from);
            const to = oneOf(STATUSES, details.to);
            if (!from || !to) return null;
            return {
                ...base,
                type: "status_changed",
                from,
                to,
                cancelledBy: oneOf(ACTORS, details.cancelled_by),
                previouslyCancelledBy: oneOf(ACTORS, details.previously_cancelled_by),
            };
        }
        case "payment_status_changed": {
            const from = oneOf(PAYMENT_STATUSES, details.from);
            const to = oneOf(PAYMENT_STATUSES, details.to);
            const source = oneOf(PAYMENT_SOURCES, details.source);
            if (!from || !to || !source) return null;
            return { ...base, type: "payment_status_changed", from, to, source };
        }
        case "rescheduled": {
            const fromStart = text(details.from_start);
            const toStart = text(details.to_start);
            if (!fromStart || !toStart) return null;
            return { ...base, type: "rescheduled", fromStart, toStart };
        }
        case "notification": {
            const kind = oneOf(KINDS, details.kind);
            const channel = oneOf(CHANNELS, details.channel);
            const recipient = oneOf(RECIPIENTS, details.recipient);
            const outcome = oneOf(OUTCOMES, details.outcome);
            if (!kind || !channel || !recipient || !outcome) return null;
            const recipientCount = typeof details.recipient_count === "number" ? details.recipient_count : 1;
            return { ...base, type: "notification", kind, channel, recipient, outcome, recipientCount };
        }
        default:
            return null;
    }
}

export function toSessionEvents(dtos: SessionEventDTO[]): SessionEvent[] {
    return dtos.map(toSessionEvent).filter((event): event is SessionEvent => event !== null);
}

export const SessionEventsAPI = {
    /** The therapist's own session. */
    forSession: async (sessionId: string): Promise<SessionEvent[]> => {
        const response = await apiClient.get<SessionEventDTO[]>(`/sessions/${sessionId}/events`);
        return toSessionEvents(response.data);
    },
    /** The therapist opened the patient's WhatsApp chat from the agenda. */
    recordManualWhatsApp: async (sessionId: string): Promise<void> => {
        await apiClient.post(`/sessions/${sessionId}/events/manual-whatsapp`);
    },
    /** A session on a centre's agenda, read by one of its administrators. */
    forCenterSession: async (centerId: string, sessionId: string): Promise<SessionEvent[]> => {
        const response = await apiClient.get<SessionEventDTO[]>(`/centers/${centerId}/sessions/${sessionId}/events`);
        return toSessionEvents(response.data);
    },
};
